import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { ObjectState } from '../interfaces/entity';
import { IProcessTypeList, ProcessType} from '../models/process-type';
import { BaseRestService } from './base-rest.service';
import { DataFieldService } from './data-field.service';
import { NavigationHelper } from './navigation-helper';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { DataField } from '../models/data-field';

// const cudOptions = { headers: new Headers({ 'Content-Type': 'application/json' }) };

@AutoUnsubscribe()
@Injectable({ providedIn: 'root'})
export class ProcessTypeService extends BaseRestService<ProcessType, IProcessTypeList> implements OnDestroy{

  onDataFieldsChanged: BehaviorSubject<any> = new BehaviorSubject([]);

  dataFieldsChanged: Subscription;

  constructor(http: HttpClient, private dataFieldService: DataFieldService, protected router: Router) {
    super(ProcessTypeService, http, 'api/processtype', 'processtype', router);

    // enable caching
    this.cacheEnabled = true;

    this.dataFieldsChanged = this.dataFieldService.onDataFieldsChanged.subscribe((response) => {
      if (this.currentItem && (response.parent.uid === this.currentItem.uid)) {
        this.currentItem.dataFields = response.dataFields;
        this.onDataFieldsChanged.next(this.currentItem.dataFields);
      }
    });
  }

  ngOnDestroy(){
    this.dataFieldsChanged?.unsubscribe();
  }

   // get first process type that matches name.  Will 404 if id not found
   getByName(name: string): Observable<ProcessType> {
    this.debug('Get Process Type by Name:', name);
    return this.getAllData().pipe(map((processTypes) => {
      return (processTypes) ? processTypes.find((x) => x.name.toLowerCase() === name.toLowerCase()) : undefined;
    }));
  }

  save(entity: ProcessType, successMessage?: string) {
    // make sure that any new data fields do not have id set otherwise they won't insert into the database properly
    entity.dataFields.forEach((x) => {
      if (!x.objectState){
        x.objectState = ObjectState.Unchanged;
      }
      if (x.objectState === ObjectState.Added){
        x.id = null;
      }
    });
    entity.processTypeTemplates.forEach((x) => {
      if (x.template){
        x.template.objectState = ((x.template.id) ? ObjectState.Modified : ObjectState.Added);
      }
    });
    return super.save(entity, successMessage);
  }

  afterSave(entity) {
    // make sure objectState is reset on datafields
    entity.dataFields.forEach((x) => {
      if (x.objectState !== ObjectState.Deleted)
      {
        x.objectState = ObjectState.Unchanged;
      }
    });

    // get an up to date list of available process types...
    this.getAllData().pipe(take(1)).subscribe((processTypes) => {
      // set the relevant property in the navigation helper
      NavigationHelper.processTypes = processTypes.filter((x) => !x.hideRecord);
      // call updateNavigation to ensure the navbar is updated to reflect any changes
      NavigationHelper.updateNavigation();
    });
  }

  getEqualityMonitoringFieldsToExclude(): Observable<DataField[]> {
    return this.getAllData().pipe(
      map((processTypes) => {
        let result: DataField[] = [];
        const equalityMonitoringProcesses = processTypes.filter((x) => x?.isFTSUDemographics);
        const equalityMonitoringProcessesLength = equalityMonitoringProcesses?.length;
        if (equalityMonitoringProcessesLength) {
          for (let i = 0; i < equalityMonitoringProcessesLength; i++) {
            const dataFields = equalityMonitoringProcesses[i]?.dataFields;
            if (dataFields) {
              result = [...result, ...dataFields];
            }
          }
        }
        return result;
      })
    );
  }
}
